import ClassicEditor from "@ckeditor/ckeditor5-build-classic";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import { yupResolver } from "@hookform/resolvers/yup";
import React, { useEffect, useState } from "react";
import moment from "moment";
import {
  Breadcrumb,
  Card,
  Col,
  Container,
  Form,
  FormCheck,
  Row,
} from "react-bootstrap";
import Flatpickr from "react-flatpickr";
import { useForm } from "react-hook-form";
import * as Yup from "yup";
import FormProvider from "../../../FunctionalComponent/FormElement/FormProvider";
import RHFTextField from "../../../FunctionalComponent/FormElement/RHTextField";
import RHSelectField from "../../../FunctionalComponent/FormElement/RHSelectField";
import RHFDatePicker from "../../../FunctionalComponent/FormElement/RHDatePicker";
import RHTextarea from "../../../FunctionalComponent/FormElement/RHTextarea";
import {
  getProductInfos,
  setProductCurrentStep,
  setProductInfo,
  useCreateProductMutation,
  useProductShowQuery,
  useUniqueColorProductInfoQuery,
} from "../../../Redux/ProductSlice";
import { toast } from "react-toastify";
import { useCategoryTreeMutation } from "../../../Redux/category/categoryApi";
import { useDispatch, useSelector } from "react-redux";
import CategoryTree from "./CategoryTree";
import {
  selectedCategoryIds,
  setSelectedCategory,
} from "../../../Redux/CategorySlice";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import useDebounce from "../../../hooks/useDebounce";

import { API_URL } from '../../../Redux/apiSlice';
import SizeChartBuilder from "./SizeChartBuilder";
import { DEFAULT_SIZE_CHARTS_BY_TYPE } from "../../../constants/sizeCharts.js";

const DESIGNER_API = `${API_URL}/designer/by-category?name=`;

const stepOneSchema = Yup.object().shape({
  productTitle: Yup.string()
    .required()
    .strict(true)
    .trim("White spaces before and after are not allow"),
  productType: Yup.string().required(),
  slug: Yup.string().matches(
    /^\S+$/,
    "String should contain at least one non-space character"
  ),
  // SKU: Yup.string().required(),
  barcode: Yup.string().required(),
  manufactureDate: Yup.string().required(),
  isActive: Yup.string().required(),
  // visibility: Yup.string().required(),
  MRP: Yup.number()
    .typeError("Input must be a valid number")
    .positive("Input must be a positive number")
    .required("Input is required"),
  sellingPrice: Yup.number()
    .typeError("Input must be a valid number")
    .positive("Input must be a positive number")
    .required("Input is required"),
  // discount: Yup.number().typeError('Input must be a valid number').notRequired(),
  // catId: Yup.array().required().min(1, 'Value is required'),
  productDetails: Yup.string().required(),
  shortDescription: Yup.string().required(),
  metaTitle: Yup.string()
    .strict(true)
    .trim("White spaces before and after are not allow"),
  metaTag: Yup.string()
    .strict(true)
    .trim("White spaces before and after are not allow"),
  metaDescription: Yup.string()
    .strict(true)
    .trim("White spaces before and after are not allow"),
  gender: Yup.string().required(),

  designerId: Yup.string().when("productType", {
    is: (val) => val === "jewellery" || val === "garment",
    then: (schema) => schema.required("Designer is required"),
  }),
});

const validateWith = {
  shouldDirty: true,
  shouldTouch: true,
  shouldValidate: true,
};

const normalizeRows = (rows, columnCount) => {
  const count = columnCount > 0 ? columnCount : 1;
  if (!Array.isArray(rows) || !rows.length) {
    return [new Array(count).fill("")];
  }
  return rows.map((row) =>
    Array.from({ length: count }, (_, idx) => `${row?.[idx] ?? ""}`)
  );
};

const normalizeChartsFromApi = (charts = []) =>
  charts.map((chart, idx) => {
    const columns =
      Array.isArray(chart?.data?.columns) && chart.data.columns.length
        ? chart.data.columns
        : ["Size", "Value"];
    return {
      id: chart.id || `chart-${idx}`,
      title: chart.title || "",
      unit: chart.unit || "",
      locale: chart.locale || "en",
      is_default: !!chart.is_default,
      sort_order:
        Number.isFinite(Number(chart.sort_order)) && chart.sort_order !== ""
          ? Number(chart.sort_order)
          : idx,
      data: {
        columns,
        rows: normalizeRows(chart?.data?.rows, columns.length),
        notes: chart?.data?.notes || "",
      },
    };
  });

const mapChartsForPayload = (charts = []) =>
  charts
    .map((chart, idx) => {
      const title = (chart?.title || "").trim();
      const columns = (chart?.data?.columns || [])
        .map((col) => `${col ?? ""}`.trim())
        .filter(Boolean);

      if (!title || !columns.length) {
        return null;
      }

      const rows = normalizeRows(chart?.data?.rows, columns.length).filter(
        (row) => row.some((cell) => cell.length)
      );

      if (!rows.length) {
        return null;
      }

      const formattedRows = rows.map((row) =>
        row.map((cell) => {
          const trimmed = cell?.trim?.() ?? "";
          if (trimmed === "") {
            return "";
          }
          const numericValue = Number(trimmed);
          return Number.isFinite(numericValue) ? numericValue : trimmed;
        })
      );

      return {
        title,
        unit: (chart?.unit || "").trim(),
        locale: ((chart?.locale || "") || "en").trim() || "en",
        is_default: !!chart?.is_default,
        sort_order:
          Number.isFinite(Number(chart?.sort_order)) && chart?.sort_order !== ""
            ? Number(chart.sort_order)
            : idx,
        data: {
          columns,
          rows: formattedRows,
          notes: (chart?.data?.notes || "").trim(),
        },
      };
    })
    .filter(Boolean);

const StepOneProduct = () => {
  const navigate = useNavigate();

  // const { productId } = useParams();

  const dispatch = useDispatch();

  const [designerList, setDesignerList] = useState([]);
  // const { } = useProductShowQuery(productId, { skip: !productId });

  const [createProductFn, { data, isSuccess, error, isLoading }] =
    useCreateProductMutation();
  const [
    getCategoryTreeFn,
    { data: categoryTreeData, isLoading: categoryLoading },
  ] = useCategoryTreeMutation();
  const catId = useSelector(selectedCategoryIds);
  const getProducInfo = useSelector(getProductInfos);

  const { pathname } = useLocation();
  const [isExit, setIsExit] = useState(false);
  const [sizeCharts, setSizeCharts] = useState([]);
  const today = new Date();

  const month = (today.getMonth() + 1).toString().padStart(2, "0"); // Months are zero-based
  const day = today.getDate().toString().padStart(2, "0");
  const year = today.getFullYear();

  // return `${ year }-${ month } -${ day } `;

  const defaultValues = {
    id: "",
    productTitle: "",
    productType: "",
    slug: "",
    // SKU: '',
    barcode: "",
    manufactureDate: `${year} -${month} -${day} `,
    isActive: true,
    visibility: "2",
    publicDateAndTime: `${year} -${month} -${day} `,
    MRP: "",
    discount: "",
    sellingPrice: "",
    productDetails: "",
    shortDescription: "",
    metaTitle: "",
    metaTag: "",
    metaDescription: "",
    isDraft: false,
    isPublic: false,
    productUniqueCode: "",
    // publishTime: "",
    catId: [],
    isHot: false,
    isTrending: false,
    isShowInDesigner: false,
    gender: "",
    similarColourUniqueCode: "",
    brand: "KnightHunt",
    designerId: "",
  };

  const methods = useForm({
    resolver: yupResolver(stepOneSchema),
    defaultValues,
    mode: "all",
  });

  const {
    watch,
    setError,
    setValue,
    handleSubmit,
    getValues,
    reset,
    formState: { errors },
  } = methods;

  const productType = watch("productType");
  const isEditMode = pathname.includes("/product-edit");

  // Auto-apply default size chart templates based on product type.
  // - In create mode: always replace with the template when product type changes.
  // - In edit mode: only apply a template if the product has no saved size charts.
  useEffect(() => {
    if (!productType) return;

    const templates = DEFAULT_SIZE_CHARTS_BY_TYPE?.[productType];
    if (!Array.isArray(templates) || !templates.length) return;

    if (!isEditMode) {
      // Product create: always sync to the selected product type
      setSizeCharts(normalizeChartsFromApi(templates));
      return;
    }

    // Product edit: only auto-fill when there is no existing chart for this product
    if (!sizeCharts.length) {
      setSizeCharts(normalizeChartsFromApi(templates));
    }
  }, [productType, isEditMode, sizeCharts.length]);

  useEffect(() => {
    if (!productType) {
      setDesignerList([]);  // clear list when empty
      return;
    }

    const category =
      productType === "jewellery" ? "jewellery" : "garments";

    fetch(`${DESIGNER_API}${category} `)
      .then((res) => res.json())
      .then((res) => {
        console.log("DESIGNER API RESULT:", res);

        if (Array.isArray(res?.response)) {
          setDesignerList(res.response);
        } else {
          setDesignerList([]); // fallback
        }
      })
      .catch(() => setDesignerList([]));
  }, [productType]);



  const onSubmit = async (data) => {
    if (catId?.length < 2) {
      toast.error(
        "Category is Required. You have to select at least one category."
      );
      return;
    }
    // console.log(data);
    // console.log(getValues("isShowInDesigner"));

    try {
      const discountedValue =
        getValues("MRP") - getValues("sellingPrice");

      const sizeChartPayload = mapChartsForPayload(sizeCharts);

      const payload = {
        ...data,
        discount: discountedValue,
        catId,
        designerId: data.designerId,
        isShowInDesigner: getValues("isShowInDesigner"),
      };

      if (sizeChartPayload.length) {
        payload.size_charts = sizeChartPayload;
      }

      createProductFn(payload);
    } catch (error) {
      setError("afterSubmit", {
        ...error,
        message: error.message,
      });
    };
  }

  useEffect(() => {
    if (isSuccess) {
      const message = data?.message || "Product created successfully";
      toast.success(message);

      if (!isExit) {
        dispatch(setProductCurrentStep("STEP2"));
      } else {
        navigate("/products-list");
      }
    }
    if (error) {
      if ("data" in error) {
        const errorData = error;
        toast.error(errorData.data.message);
      }
    }
  }, [data, isSuccess, error, pathname]);

  useEffect(() => {
    getCategoryTreeFn();
  }, []);

  useEffect(() => {
    if (!pathname.includes("/product-edit")) {
      setSizeCharts([]);
      return;
    }

    if (getProducInfo?.productId || getProducInfo?.id) {
      for (const key in defaultValues) {
        if (Object.hasOwnProperty.call(defaultValues, key)) {
          if (key === "publicDateAndTime") {
            setValue(
              key,
              moment(getProducInfo?.[key]).format("YYYY-MM-DD"),
              validateWith
            );
          } else {
            setValue(key, getProducInfo?.[key], validateWith);
          }
        }
      }

      const apiSizeCharts =
        getProducInfo?.size_charts || getProducInfo?.sizeCharts;
      if (Array.isArray(apiSizeCharts)) {
        setSizeCharts(normalizeChartsFromApi(apiSizeCharts));
      } else {
        setSizeCharts([]);
      }
    }
  }, [getProducInfo, pathname]);

  const yy = watch();
  const similarColourUniqueCode = watch("similarColourUniqueCode");
  const discountValue = (getValues("MRP") - getValues("sellingPrice")).toFixed(
    2
  );
  // const flattenItems = (item) => {
  //     if (!item.Children) {
  //         return [item];
  //     }

  //     const forrmattedChild = item.Children.map(c => ({ ...c, pId: item?.id }))
  //     return [item, ...item.Children.flatMap(flattenItems)];
  // };
  // const flattened = categoryTreeData?.response?.flatMap(flattenItems);
  // console.log(flattened, "categoryTreeData?.response");

  // console.log('categoryTreeData?.response', categoryTreeData?.response?.flatMap((c) => {
  //     return c?.Children

  // })

  const [searchText, setSearchText] = useState("");
  const debounceText = useDebounce(searchText, 500);

  // const { data: useUniqueColorProductInfo } = useUniqueColorProductInfoQuery(debounceText, { skip: !debounceText });

  useEffect(() => {
    if (similarColourUniqueCode) {
      setSearchText(similarColourUniqueCode);
    }
  }, [similarColourUniqueCode]);

  // useEffect(() => {
  //     if (useUniqueColorProductInfo?.id) {
  //         Object.keys(defaultValues).map(item => {
  //             setValue(item, useUniqueColorProductInfo?.[item])
  //         })
  //         dispatch(setSelectedCategory([]))
  //     }

  // }, [useUniqueColorProductInfo])

  return (
    <>
      <Breadcrumb title="Create Product" pageTitle="Products" />
      <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
        <Row>
          <Col xl={9} lg={8}>
            <Card>
              <Card.Header>
                <div className="d-flex">
                  <div className="flex-shrink-0 me-3">
                    <div className="avatar-sm">
                      <div className="avatar-title rounded-circle bg-light text-primary fs-20">
                        <i className="ri-file-info-line"></i>
                      </div>
                    </div>
                  </div>
                  <div className="flex-grow-1 d-flex align-items-center">
                    <h5 className="card-title mb-1">Product Information</h5>
                  </div>
                </div>
              </Card.Header>
              <Card.Body>
                <Row>
                  <Col xl={3} lg={6} className="mb-3">
                    <RHFTextField
                      name="productTitle"
                      label="Product title"
                      required
                      onChange={(e) => {
                        setValue("productTitle", e.target.value, validateWith);
                        setValue(
                          "slug",
                          e.target.value
                            .toLowerCase()
                            .replace(/&/g, "and")
                            .replace(/ /g, "-"),
                          validateWith
                        );
                      }}
                    />
                  </Col>
                  <Col xl={3} lg={6} className="mb-3">
                    {/* <Form.Label htmlFor="product-title-input">Status</Form.Label>
                                        <Form.Select aria-label="Default select example">
                                            <option selected>select a Status</option>
                                            <option value="1">Active</option>
                                            <option value="2">Inactive</option>
                                        </Form.Select>
                                        <div className="invalid-feedback">Please enter a status.</div> */}

                    <RHSelectField
                      name="productType"
                      required
                      label="Product Type"
                      list={[
                        { name: "Garment", value: "garment" },
                        { name: "Jewellery", value: "jewellery" },
                      ]}
                    />
                  </Col>

                  {watch("productType") && (
                    <Col xl={3} lg={6} className="mb-3">
                      <RHSelectField
                        name="designerId"
                        label="Designer"
                        required
                        list={
                          Array.isArray(designerList)
                            ? designerList.map((d) => ({
                              name: d.name,
                              value: d.id,
                            }))
                            : []
                        }
                      />
                    </Col>
                  )}

                  <Col xl={3} lg={6} className="mb-3">
                    <RHFTextField name="slug" label="Slag URL" required />
                  </Col>
                  <Col xl={3} lg={6} className="mb-3">
                    <RHFTextField name="brand" label="brand" />
                  </Col>
                  <Col xl={3} lg={6} className="mb-3">
                    <RHFTextField
                      name="productUniqueCode"
                      label="Product Unique Code"
                      disabled
                    />
                  </Col>
                  <Col xl={3} lg={6} className="mb-3">
                    <RHFTextField name="barcode" label="Barcode" required />
                  </Col>
                  <Col xl={3} lg={6} className="mb-3 mb-xl-0">
                    <RHFDatePicker
                      label="Manufacture Date"
                      name="manufactureDate"
                      required
                    />
                  </Col>
                  <Col xl={3} lg={6} className="mb-3">
                    {/* <Form.Label htmlFor="product-title-input">Status</Form.Label>
                                        <Form.Select aria-label="Default select example">
                                            <option selected>select a Status</option>
                                            <option value="1">Active</option>
                                            <option value="2">Inactive</option>
                                        </Form.Select>
                                        <div className="invalid-feedback">Please enter a status.</div> */}

                    <RHSelectField
                      name="isActive"
                      required
                      label="Status"
                      list={[
                        { name: "Active", value: true },
                        { name: "Inactive", value: false },
                      ]}
                    />
                  </Col>
                  <Col xl={3} lg={6} className="mb-3">
                    <RHSelectField
                      name="visibility"
                      label="Visibility"
                      list={[
                        { name: "Hidden", value: 1 },
                        { name: "Public", value: 2 },
                        { name: "Schedule", value: 3 },
                        { name: "Out of Stock", value: 4 },
                      ]}
                    />
                  </Col>
                  <Col xl={3} lg={6} className="mb-3">
                    <RHFDatePicker
                      label="Public date & Time"
                      name="publicDateAndTime"
                      setValue={setValue}
                    />
                  </Col>
                  <Col xl={3} lg={6} className="mb-3">
                    {/* <Form.Label htmlFor="product-title-input">Status</Form.Label>
                                        <Form.Select aria-label="Default select example">
                                            <option selected>select a Status</option>
                                            <option value="1">Active</option>
                                            <option value="2">Inactive</option>
                                        </Form.Select>
                                        <div className="invalid-feedback">Please enter a status.</div> */}

                    <RHSelectField
                      name="gender"
                      required
                      label="Gender"
                      list={[
                        { name: "Men", value: "men" },
                        { name: "Women", value: "women" },
                        { name: "Unisex", value: "unisex" },
                      ]}
                    />
                  </Col>
                  {/* <Col xl={3} lg={6} className="mb-3">
                    <RHFTextField name='similarColourUniqueCode' label='Similar Colour Unique Code' required />
                  </Col> */}
                </Row>
              </Card.Body>
            </Card>
            <Card>
              <Card.Header>
                <div className="d-flex">
                  <div className="flex-shrink-0 me-3">
                    <div className="avatar-sm">
                      <div className="avatar-title rounded-circle bg-light text-primary fs-20">
                        <i className="ri-price-tag-3-line"></i>
                      </div>
                    </div>
                  </div>
                  <div className="flex-grow-1 d-flex align-items-center">
                    <h5 className="card-title mb-1">Product Price</h5>
                  </div>
                </div>
              </Card.Header>
              <Card.Body>
                <Row>
                  <Col xl={4} lg={6} className="mb-3">
                    <RHFTextField
                      name="MRP"
                      label="Maximum Retail Price"
                      placeholder="₹ 00.00"
                      required
                      onChange={(e) => {
                        const priceInput = e.target.value;
                        if (!isNaN(priceInput)) {
                          if (priceInput >= Number(getValues("sellingPrice")))
                            setValue("MRP", priceInput, validateWith);
                        }
                      }}
                    />
                  </Col>
                  <Col xl={4} lg={6} className="mb-3">
                    <RHFTextField
                      name="sellingPrice"
                      label="Selling Price"
                      placeholder="₹ 00.00"
                      required
                      type="number"
                      onChange={(e) => {
                        const priceInput = e.target.value;
                        if (!isNaN(priceInput)) {
                          if (priceInput <= Number(getValues("MRP")))
                            setValue("sellingPrice", priceInput, validateWith);
                        }
                      }}
                    />
                  </Col>
                  <Col xl={4} lg={6} className="mb-xl-3">
                    <RHFTextField
                      name="discount"
                      value={discountValue}
                      disabled={true}
                      label="Discount"
                      placeholder="₹ 00.00"
                    />
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            <SizeChartBuilder charts={sizeCharts} onChange={setSizeCharts} />

            <Card>
              <Card.Header>
                <div className="d-flex">
                  <div className="flex-shrink-0 me-3">
                    <div className="avatar-sm">
                      <div className="avatar-title rounded-circle bg-light text-primary fs-20">
                        <i className="ri-file-list-3-line"></i>
                      </div>
                    </div>
                  </div>
                  <div className="flex-grow-1 d-flex align-items-center">
                    <h5 className="card-title mb-1">
                      Product Details <span className="text-danger">*</span>{" "}
                    </h5>
                  </div>
                </div>
              </Card.Header>
              <Card.Body>
                <div className="mb-3">
                  <Form.Label>
                    Product Details <span className="text-danger">*</span>
                  </Form.Label>
                  <CKEditor
                    defaultValue={getValues("productDetails")}
                    value={getValues("productDetails")}
                    editor={ClassicEditor}
                    data={getValues("productDetails")}
                    onChange={(event, editor) =>
                      setValue("productDetails", editor.getData(), validateWith)
                    }
                  />
                  {errors.productDetails?.message && (
                    <div className="text-denger">
                      {errors.productDetails?.message}
                    </div>
                  )}

                  {/* <RHTextarea name='productDetails' placeholder="Must enter minimum of a 100 characters" rows={3} /> */}
                </div>
              </Card.Body>
            </Card>

            <Card>
              <Card.Header>
                <div className="d-flex">
                  <div className="flex-shrink-0 me-3">
                    <div className="avatar-sm">
                      <div className="avatar-title rounded-circle bg-light text-primary fs-20">
                        <i className="ri-search-eye-line"></i>
                      </div>
                    </div>
                  </div>
                  <div className="flex-grow-1 d-flex align-items-center">
                    <h5 className="card-title mb-1">
                      Search Engine Optimization (SEO)
                    </h5>
                  </div>
                </div>
              </Card.Header>
              <Card.Body>
                <Row>
                  <Col lg={6} className="mb-3">
                    {/* <Form.Label htmlFor="product-title-input">Meta Title</Form.Label>
                                        <Form.Control type="hidden" id="formAction" name="formAction" defaultValue="add" />
                                        <Form.Control type="text" className="d-none" id="product-id-input" />
                                        <Form.Control type="text" id="product-title-input" defaultValue="" placeholder="Add a meta title" required />
                                        <div className="invalid-feedback">Please enter a meta title.</div> */}
                    <RHFTextField
                      name="metaTitle"
                      label="Meta Title"
                      placeholder="Add a meta title"
                    />
                  </Col>
                  <Col lg={6} className="mb-3">
                    {/* <Form.Label htmlFor="product-title-input">Meta Tag</Form.Label>
                                        <Form.Control type="hidden" id="formAction" name="formAction" defaultValue="add" />
                                        <Form.Control type="text" className="d-none" id="product-id-input" />
                                        <Form.Control type="text" id="product-title-input" defaultValue="" placeholder="Add a meta tag" required />
                                        <div className="invalid-feedback">Please enter a meta tag.</div> */}
                    <RHFTextField
                      name="metaTag"
                      label="Meta Tag"
                      placeholder="Add a meta metaTag"
                    />
                  </Col>
                  <Col className="mb-xl-3">
                    {/* <Form.Group>
                                            <Form.Label>Meta Description</Form.Label>
                                            <textarea className="form-control" id="exampleFormControlTextarea5" placeholder="Add meta description" rows={3}></textarea>
                                        </Form.Group>
                                        <div className="invalid-feedback">Please enter meta dscription.</div> */}
                    <RHTextarea
                      label="Meta Description"
                      name="metaDescription"
                      placeholder="Add meta description"
                      rows={3}
                    />
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </Col>

          <Col xl={3} lg={4}>
            <Card>
              <Card.Header>
                <h5 className="card-title mb-0">
                  Product Category<span className="text-danger">*</span>
                </h5>
              </Card.Header>
              <Card.Body>
                <CategoryTree
                  // error={errors.catId?.message}
                  data={categoryTreeData?.response}
                  defaultChecked={false}
                  className="product-parent-category"
                />
              </Card.Body>
            </Card>

            {/* <Card>
                            <Card.Header>
                                <h5 className="card-title mb-0">Publish Schedule</h5>
                            </Card.Header>

                            <Card.Body>
                                <div>
                                    <Form.Label htmlFor="datepicker-publish-input">Publish Date & Time</Form.Label>
                                    <Flatpickr
                                        className="form-control flatpickr-input"
                                        placeholder='Enter publish date'
                                        options={{
                                            dateFormat: "d M, Y",
                                        }}
                                        onChange={(e, date) => {
                                            const formattedDate = moment(e?.[0]).format('L');

                                            setValue('publishTime', e?.[0], validateWith)

                                        }}
                                    />
                                </div>
                            </Card.Body>
                        </Card> */}

            {/* <Card>
                            <Card.Header>
                                <h5 className="card-title mb-0">Product Tags</h5>
                            </Card.Header>
                            <Card.Body>
                                <div className="hstack gap-3 align-items-start">
                                    <div className="flex-grow-1">
                                        <RHFTextField name='metaTag' placeholder="Enter tags" />
                                    </div>
                                </div>
                            </Card.Body>
                        </Card> */}

            <Card>
              <Card.Header>
                <h5 className="card-title mb-0">
                  Product Short Description
                  <span className="text-danger">*</span>
                </h5>
              </Card.Header>
              <Card.Body>
                <p className="text-muted mb-2">
                  Add short description for product
                </p>
                <RHTextarea
                  name="shortDescription"
                  placeholder="Must enter minimum of a 100 characters"
                  rows={3}
                />
                {/* <textarea className="form-control" placeholder="Must enter minimum of a 100 characters" rows={3}></textarea> */}
              </Card.Body>
            </Card>

            <div className="d-flex ">
              <Form.Check
                onChange={(e) => {
                  setValue("isHot", e.target.checked, validateWith);
                }}
                className="mt-0 me-2"
                type="checkbox"
                defaultValue=""
                id="isHot"
                aria-label="Checkbox for following text input"
                checked={getValues("isHot") === true}
              />
              <label htmlFor="isHot">Hot Product</label>
            </div>

            <div className="d-flex ">
              <Form.Check
                onChange={(e) => {
                  setValue("isTrending", e.target.checked, validateWith);
                }}
                className="mt-0 me-2"
                type="checkbox"
                defaultValue=""
                id="isTrending"
                aria-label="Checkbox for following text input"
                checked={getValues("isTrending") === true}
              />
              <label htmlFor="isTrending">Trending Product</label>
            </div>


            <div className="d-flex ">
              <Form.Check
                onChange={(e) => {
                  setValue("isShowInDesigner", e.target.checked, validateWith);
                }}
                className="mt-0 me-2"
                type="checkbox"
                defaultValue=""
                id="isShowInDesigner"
                aria-label="Checkbox for following text input"
                checked={getValues("isShowInDesigner") === true}
              />
              <label htmlFor="isShowInDesigner">Show In Designer</label>
            </div>
          </Col>
        </Row>
        <Container fluid>
          <div className="d-flex">
            <div className="ms-auto">
              <button
                className="btn btn-info shadow-lg rounded"
                type="submit"
                onClick={() => {
                  setIsExit(true);
                }}
              >
                Draft & Exit
              </button>
              <button
                className="btn  btn-danger shadow-lg rounded ms-2"
                onClick={() => navigate("/products-list")}
              >
                Cancel
              </button>
              <button
                className="btn btn-secondary shadow-lg rounded ms-2"
                type="submit"
                disabled={isLoading}
              >
                Next
              </button>
            </div>
          </div>
        </Container>
      </FormProvider>
    </>
  );
};

export default StepOneProduct;





//USING CATEGOEY

// import ClassicEditor from "@ckeditor/ckeditor5-build-classic";
// import { CKEditor } from "@ckeditor/ckeditor5-react";
// import { yupResolver } from "@hookform/resolvers/yup";
// import React, { useEffect, useState } from "react";
// import moment from "moment";
// import {
//   Breadcrumb,
//   Card,
//   Col,
//   Container,
//   Form,
//   FormCheck,
//   Row,
// } from "react-bootstrap";
// import Flatpickr from "react-flatpickr";
// import { useForm } from "react-hook-form";
// import * as Yup from "yup";
// import FormProvider from "../../../FunctionalComponent/FormElement/FormProvider";
// import RHFTextField from "../../../FunctionalComponent/FormElement/RHTextField";
// import RHSelectField from "../../../FunctionalComponent/FormElement/RHSelectField";
// import RHFDatePicker from "../../../FunctionalComponent/FormElement/RHDatePicker";
// import RHTextarea from "../../../FunctionalComponent/FormElement/RHTextarea";
// import {
//   getProductInfos,
//   setProductCurrentStep,
//   setProductInfo,
//   useCreateProductMutation,
//   useProductShowQuery,
//   useUniqueColorProductInfoQuery,
// } from "../../../Redux/ProductSlice";
// import { toast } from "react-toastify";
// import { useCategoryTreeMutation } from "../../../Redux/category/categoryApi";
// import { useDispatch, useSelector } from "react-redux";
// import CategoryTree from "./CategoryTree";
// import {
//   selectedCategoryIds,
//   setSelectedCategory,
// } from "../../../Redux/CategorySlice";
// import { useLocation, useNavigate, useParams } from "react-router-dom";
// import useDebounce from "../../../hooks/useDebounce";

// import { API_URL } from '../../../Redux/apiSlice';
// import SizeChartBuilder from "./SizeChartBuilder";
// import { DEFAULT_SIZE_CHARTS_BY_TYPE } from "../../../constants/sizeCharts.js";

// const DESIGNER_API = `${API_URL}/designer/by-category?name=`;
// const PRODUCT_TYPE_API = `${API_URL}/category/slug/top-designers`;
// const DESIGNER_TREE_API = `${API_URL}/category/slug-tree/top-designers`;

// const stepOneSchema = Yup.object().shape({
//   productTitle: Yup.string()
//     .required()
//     .strict(true)
//     .trim("White spaces before and after are not allow"),
//   productType: Yup.string().required(),
//   slug: Yup.string().matches(
//     /^\S+$/,
//     "String should contain at least one non-space character"
//   ),
//   // SKU: Yup.string().required(),
//   barcode: Yup.string().required(),
//   manufactureDate: Yup.string().required(),
//   isActive: Yup.string().required(),
//   // visibility: Yup.string().required(),
//   MRP: Yup.number()
//     .typeError("Input must be a valid number")
//     .positive("Input must be a positive number")
//     .required("Input is required"),
//   sellingPrice: Yup.number()
//     .typeError("Input must be a valid number")
//     .positive("Input must be a positive number")
//     .required("Input is required"),
//   // discount: Yup.number().typeError('Input must be a valid number').notRequired(),
//   // catId: Yup.array().required().min(1, 'Value is required'),
//   productDetails: Yup.string().required(),
//   shortDescription: Yup.string().required(),
//   metaTitle: Yup.string()
//     .strict(true)
//     .trim("White spaces before and after are not allow"),
//   metaTag: Yup.string()
//     .strict(true)
//     .trim("White spaces before and after are not allow"),
//   metaDescription: Yup.string()
//     .strict(true)
//     .trim("White spaces before and after are not allow"),
//   gender: Yup.string().required(),

//   designerId: Yup.string().when("productType", {
//     is: (val) => val === "jewels" || val === "garment",
//     then: (schema) => schema.required("Designer is required"),
//   }),
// });

// const validateWith = {
//   shouldDirty: true,
//   shouldTouch: true,
//   shouldValidate: true,
// };

// const normalizeRows = (rows, columnCount) => {
//   const count = columnCount > 0 ? columnCount : 1;
//   if (!Array.isArray(rows) || !rows.length) {
//     return [new Array(count).fill("")];
//   }
//   return rows.map((row) =>
//     Array.from({ length: count }, (_, idx) => `${row?.[idx] ?? ""}`)
//   );
// };

// const normalizeChartsFromApi = (charts = []) =>
//   charts.map((chart, idx) => {
//     const columns =
//       Array.isArray(chart?.data?.columns) && chart.data.columns.length
//         ? chart.data.columns
//         : ["Size", "Value"];
//     return {
//       id: chart.id || `chart-${idx}`,
//       title: chart.title || "",
//       unit: chart.unit || "",
//       locale: chart.locale || "en",
//       is_default: !!chart.is_default,
//       sort_order:
//         Number.isFinite(Number(chart.sort_order)) && chart.sort_order !== ""
//           ? Number(chart.sort_order)
//           : idx,
//       data: {
//         columns,
//         rows: normalizeRows(chart?.data?.rows, columns.length),
//         notes: chart?.data?.notes || "",
//       },
//     };
//   });

// const mapChartsForPayload = (charts = []) =>
//   charts
//     .map((chart, idx) => {
//       const title = (chart?.title || "").trim();
//       const columns = (chart?.data?.columns || [])
//         .map((col) => `${col ?? ""}`.trim())
//         .filter(Boolean);

//       if (!title || !columns.length) {
//         return null;
//       }

//       const rows = normalizeRows(chart?.data?.rows, columns.length).filter(
//         (row) => row.some((cell) => cell.length)
//       );

//       if (!rows.length) {
//         return null;
//       }

//       const formattedRows = rows.map((row) =>
//         row.map((cell) => {
//           const trimmed = cell?.trim?.() ?? "";
//           if (trimmed === "") {
//             return "";
//           }
//           const numericValue = Number(trimmed);
//           return Number.isFinite(numericValue) ? numericValue : trimmed;
//         })
//       );

//       return {
//         title,
//         unit: (chart?.unit || "").trim(),
//         locale: ((chart?.locale || "") || "en").trim() || "en",
//         is_default: !!chart?.is_default,
//         sort_order:
//           Number.isFinite(Number(chart?.sort_order)) && chart?.sort_order !== ""
//             ? Number(chart.sort_order)
//             : idx,
//         data: {
//           columns,
//           rows: formattedRows,
//           notes: (chart?.data?.notes || "").trim(),
//         },
//       };
//     })
//     .filter(Boolean);

// const StepOneProduct = () => {
//   const navigate = useNavigate();

//   // const { productId } = useParams();

//   const dispatch = useDispatch();

//   const [designerList, setDesignerList] = useState([]);
//   const [productTypes, setProductTypes] = useState([]);
//   // const { } = useProductShowQuery(productId, { skip: !productId });

//   const [createProductFn, { data, isSuccess, error, isLoading }] =
//     useCreateProductMutation();
//   const [
//     getCategoryTreeFn,
//     { data: categoryTreeData, isLoading: categoryLoading },
//   ] = useCategoryTreeMutation();
//   const catId = useSelector(selectedCategoryIds);
//   const getProducInfo = useSelector(getProductInfos);

//   const { pathname } = useLocation();
//   const [isExit, setIsExit] = useState(false);
//   const [sizeCharts, setSizeCharts] = useState([]);
//   const today = new Date();

//   const month = (today.getMonth() + 1).toString().padStart(2, "0"); // Months are zero-based
//   const day = today.getDate().toString().padStart(2, "0");
//   const year = today.getFullYear();

//   // return `${ year }-${ month } -${ day } `;

//   const defaultValues = {
//     id: "",
//     productTitle: "",
//     productType: "",
//     slug: "",
//     // SKU: '',
//     barcode: "",
//     manufactureDate: `${year} -${month} -${day} `,
//     isActive: true,
//     visibility: "2",
//     publicDateAndTime: `${year} -${month} -${day} `,
//     MRP: "",
//     discount: "",
//     sellingPrice: "",
//     productDetails: "",
//     shortDescription: "",
//     metaTitle: "",
//     metaTag: "",
//     metaDescription: "",
//     isDraft: false,
//     isPublic: false,
//     productUniqueCode: "",
//     // publishTime: "",
//     catId: [],
//     isHot: false,
//     isTrending: false,
//     isShowInDesigner: false,
//     gender: "",
//     similarColourUniqueCode: "",
//     brand: "KnightHunt",
//     designerId: "",
//   };

//   const methods = useForm({
//     resolver: yupResolver(stepOneSchema),
//     defaultValues,
//     mode: "all",
//   });

//   const {
//     watch,
//     setError,
//     setValue,
//     handleSubmit,
//     getValues,
//     reset,
//     formState: { errors },
//   } = methods;

//   const productType = watch("productType");
//   const isEditMode = pathname.includes("/product-edit");

//   // Auto-apply default size chart templates based on product type.
//   // - In create mode: always replace with the template when product type changes.
//   // - In edit mode: only apply a template if the product has no saved size charts.
//   useEffect(() => {
//     if (!productType) return;

//     const templates = DEFAULT_SIZE_CHARTS_BY_TYPE?.[productType];
//     if (!Array.isArray(templates) || !templates.length) return;

//     if (!isEditMode) {
//       // Product create: always sync to the selected product type
//       setSizeCharts(normalizeChartsFromApi(templates));
//       return;
//     }

//     // Product edit: only auto-fill when there is no existing chart for this product
//     if (!sizeCharts.length) {
//       setSizeCharts(normalizeChartsFromApi(templates));
//     }
//   }, [productType, isEditMode, sizeCharts.length]);

//   // useEffect(() => {
//   //   if (!productType) {
//   //     setDesignerList([]);  // clear list when empty
//   //     return;
//   //   }

//   //   const category =
//   //     productType === "jewellery" ? "jewellery" : "garments";

//   //   fetch(`${DESIGNER_API}${category} `)
//   //     .then((res) => res.json())
//   //     .then((res) => {
//   //       console.log("DESIGNER API RESULT:", res);

//   //       if (Array.isArray(res?.response)) {
//   //         setDesignerList(res.response);
//   //       } else {
//   //         setDesignerList([]); // fallback
//   //       }
//   //     })
//   //     .catch(() => setDesignerList([]));
//   // }, [productType]);

//   useEffect(() => {
//     fetch(PRODUCT_TYPE_API)
//       .then((res) => res.json())
//       .then((res) => {
//         if (Array.isArray(res?.response?.children)) {
//           setProductTypes(
//             res.response.children.map((item) => ({
//               name: item.name,
//               value: item.slug, // garment / jewels
//             }))
//           );
//         } else {
//           setProductTypes([]);
//         }
//       })
//       .catch(() => setProductTypes([]));
//   }, []);

//   useEffect(() => {
//     if (!productType) {
//       setDesignerList([]);
//       return;
//     }

//     fetch(DESIGNER_TREE_API)
//       .then((res) => res.json())
//       .then((res) => {
//         const categories = res?.response?.children || [];

//         const matchedCategory = categories.find(
//           (cat) => cat.slug === productType
//         );

//         if (matchedCategory && Array.isArray(matchedCategory.children)) {
//           setDesignerList(
//             matchedCategory.children.map((designer) => ({
//               name: designer.name,
//               value: designer.id,
//             }))
//           );
//         } else {
//           setDesignerList([]);
//         }
//       })
//       .catch(() => setDesignerList([]));
//   }, [productType]);



//   const onSubmit = async (data) => {
//     if (catId?.length < 2) {
//       toast.error(
//         "Category is Required. You have to select at least one category."
//       );
//       return;
//     }
//     // console.log(data);
//     // console.log(getValues("isShowInDesigner"));

//     try {
//       const discountedValue =
//         getValues("MRP") - getValues("sellingPrice");

//       const sizeChartPayload = mapChartsForPayload(sizeCharts);

//       const payload = {
//         ...data,
//         discount: discountedValue,
//         catId,
//         designerId: data.designerId,
//         isShowInDesigner: getValues("isShowInDesigner"),
//       };

//       if (sizeChartPayload.length) {
//         payload.size_charts = sizeChartPayload;
//       }

//       createProductFn(payload);
//     } catch (error) {
//       setError("afterSubmit", {
//         ...error,
//         message: error.message,
//       });
//     };
//   }

//   useEffect(() => {
//     if (isSuccess) {
//       const message = data?.message || "Product created successfully";
//       toast.success(message);

//       if (!isExit) {
//         dispatch(setProductCurrentStep("STEP2"));
//       } else {
//         navigate("/products-list");
//       }
//     }
//     if (error) {
//       if ("data" in error) {
//         const errorData = error;
//         toast.error(errorData.data.message);
//       }
//     }
//   }, [data, isSuccess, error, pathname]);

//   useEffect(() => {
//     getCategoryTreeFn();
//   }, []);

//   useEffect(() => {
//     if (!pathname.includes("/product-edit")) {
//       setSizeCharts([]);
//       return;
//     }

//     if (getProducInfo?.productId || getProducInfo?.id) {
//       for (const key in defaultValues) {
//         if (Object.hasOwnProperty.call(defaultValues, key)) {
//           if (key === "publicDateAndTime") {
//             setValue(
//               key,
//               moment(getProducInfo?.[key]).format("YYYY-MM-DD"),
//               validateWith
//             );
//           } else {
//             setValue(key, getProducInfo?.[key], validateWith);
//           }
//         }
//       }

//       const apiSizeCharts =
//         getProducInfo?.size_charts || getProducInfo?.sizeCharts;
//       if (Array.isArray(apiSizeCharts)) {
//         setSizeCharts(normalizeChartsFromApi(apiSizeCharts));
//       } else {
//         setSizeCharts([]);
//       }
//     }
//   }, [getProducInfo, pathname]);

//   const yy = watch();
//   const similarColourUniqueCode = watch("similarColourUniqueCode");
//   const discountValue = (getValues("MRP") - getValues("sellingPrice")).toFixed(
//     2
//   );
//   // const flattenItems = (item) => {
//   //     if (!item.Children) {
//   //         return [item];
//   //     }

//   //     const forrmattedChild = item.Children.map(c => ({ ...c, pId: item?.id }))
//   //     return [item, ...item.Children.flatMap(flattenItems)];
//   // };
//   // const flattened = categoryTreeData?.response?.flatMap(flattenItems);
//   // console.log(flattened, "categoryTreeData?.response");

//   // console.log('categoryTreeData?.response', categoryTreeData?.response?.flatMap((c) => {
//   //     return c?.Children

//   // })

//   const [searchText, setSearchText] = useState("");
//   const debounceText = useDebounce(searchText, 500);

//   // const { data: useUniqueColorProductInfo } = useUniqueColorProductInfoQuery(debounceText, { skip: !debounceText });

//   useEffect(() => {
//     if (similarColourUniqueCode) {
//       setSearchText(similarColourUniqueCode);
//     }
//   }, [similarColourUniqueCode]);

//   // useEffect(() => {
//   //     if (useUniqueColorProductInfo?.id) {
//   //         Object.keys(defaultValues).map(item => {
//   //             setValue(item, useUniqueColorProductInfo?.[item])
//   //         })
//   //         dispatch(setSelectedCategory([]))
//   //     }

//   // }, [useUniqueColorProductInfo])

//   return (
//     <>
//       <Breadcrumb title="Create Product" pageTitle="Products" />
//       <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
//         <Row>
//           <Col xl={9} lg={8}>
//             <Card>
//               <Card.Header>
//                 <div className="d-flex">
//                   <div className="flex-shrink-0 me-3">
//                     <div className="avatar-sm">
//                       <div className="avatar-title rounded-circle bg-light text-primary fs-20">
//                         <i className="ri-file-info-line"></i>
//                       </div>
//                     </div>
//                   </div>
//                   <div className="flex-grow-1 d-flex align-items-center">
//                     <h5 className="card-title mb-1">Product Information</h5>
//                   </div>
//                 </div>
//               </Card.Header>
//               <Card.Body>
//                 <Row>
//                   <Col xl={3} lg={6} className="mb-3">
//                     <RHFTextField
//                       name="productTitle"
//                       label="Product title"
//                       required
//                       onChange={(e) => {
//                         setValue("productTitle", e.target.value, validateWith);
//                         setValue(
//                           "slug",
//                           e.target.value
//                             .toLowerCase()
//                             .replace(/&/g, "and")
//                             .replace(/ /g, "-"),
//                           validateWith
//                         );
//                       }}
//                     />
//                   </Col>
//                   <Col xl={3} lg={6} className="mb-3">
//                     {/* <Form.Label htmlFor="product-title-input">Status</Form.Label>
//                                         <Form.Select aria-label="Default select example">
//                                             <option selected>select a Status</option>
//                                             <option value="1">Active</option>
//                                             <option value="2">Inactive</option>
//                                         </Form.Select>
//                                         <div className="invalid-feedback">Please enter a status.</div> */}

//                     {/* <RHSelectField
//                       name="productType"
//                       required
//                       label="Product Type"
//                       list={[
//                         { name: "Garment", value: "garment" },
//                         { name: "Jewellery", value: "jewellery" },
//                       ]}
//                     /> */}
//                     {console.log('productTypes', productTypes)}
//                     <RHSelectField
//                       name="productType"
//                       required
//                       label="Product Type"
//                       list={productTypes}
//                     />
//                   </Col>

//                   {watch("productType") && (
//                     <Col xl={3} lg={6} className="mb-3">
//                       <RHSelectField
//                         name="designerId"
//                         label="Designer"
//                         required
//                         list={
//                           Array.isArray(designerList)
//                             ? designerList.map((d) => ({
//                               name: d.name,
//                               value: d.value,
//                             }))
//                             : []
//                         }
//                       />
//                     </Col>
//                   )}


//                   <Col xl={3} lg={6} className="mb-3">
//                     <RHFTextField name="slug" label="Slag URL" required />
//                   </Col>
//                   <Col xl={3} lg={6} className="mb-3">
//                     <RHFTextField name="brand" label="brand" />
//                   </Col>
//                   <Col xl={3} lg={6} className="mb-3">
//                     <RHFTextField
//                       name="productUniqueCode"
//                       label="Product Unique Code"
//                       disabled
//                     />
//                   </Col>
//                   <Col xl={3} lg={6} className="mb-3">
//                     <RHFTextField name="barcode" label="Barcode" required />
//                   </Col>
//                   <Col xl={3} lg={6} className="mb-3 mb-xl-0">
//                     <RHFDatePicker
//                       label="Manufacture Date"
//                       name="manufactureDate"
//                       required
//                     />
//                   </Col>
//                   <Col xl={3} lg={6} className="mb-3">
//                     {/* <Form.Label htmlFor="product-title-input">Status</Form.Label>
//                                         <Form.Select aria-label="Default select example">
//                                             <option selected>select a Status</option>
//                                             <option value="1">Active</option>
//                                             <option value="2">Inactive</option>
//                                         </Form.Select>
//                                         <div className="invalid-feedback">Please enter a status.</div> */}

//                     <RHSelectField
//                       name="isActive"
//                       required
//                       label="Status"
//                       list={[
//                         { name: "Active", value: true },
//                         { name: "Inactive", value: false },
//                       ]}
//                     />
//                   </Col>
//                   <Col xl={3} lg={6} className="mb-3">
//                     <RHSelectField
//                       name="visibility"
//                       label="Visibility"
//                       list={[
//                         { name: "Hidden", value: 1 },
//                         { name: "Public", value: 2 },
//                         { name: "Schedule", value: 3 },
//                         { name: "Out of Stock", value: 4 },
//                       ]}
//                     />
//                   </Col>
//                   <Col xl={3} lg={6} className="mb-3">
//                     <RHFDatePicker
//                       label="Public date & Time"
//                       name="publicDateAndTime"
//                       setValue={setValue}
//                     />
//                   </Col>
//                   <Col xl={3} lg={6} className="mb-3">
//                     {/* <Form.Label htmlFor="product-title-input">Status</Form.Label>
//                                         <Form.Select aria-label="Default select example">
//                                             <option selected>select a Status</option>
//                                             <option value="1">Active</option>
//                                             <option value="2">Inactive</option>
//                                         </Form.Select>
//                                         <div className="invalid-feedback">Please enter a status.</div> */}

//                     <RHSelectField
//                       name="gender"
//                       required
//                       label="Gender"
//                       list={[
//                         { name: "Men", value: "men" },
//                         { name: "Women", value: "women" },
//                         { name: "Unisex", value: "unisex" },
//                       ]}
//                     />
//                   </Col>
//                   {/* <Col xl={3} lg={6} className="mb-3">
//                     <RHFTextField name='similarColourUniqueCode' label='Similar Colour Unique Code' required />
//                   </Col> */}
//                 </Row>
//               </Card.Body>
//             </Card>
//             <Card>
//               <Card.Header>
//                 <div className="d-flex">
//                   <div className="flex-shrink-0 me-3">
//                     <div className="avatar-sm">
//                       <div className="avatar-title rounded-circle bg-light text-primary fs-20">
//                         <i className="ri-price-tag-3-line"></i>
//                       </div>
//                     </div>
//                   </div>
//                   <div className="flex-grow-1 d-flex align-items-center">
//                     <h5 className="card-title mb-1">Product Price</h5>
//                   </div>
//                 </div>
//               </Card.Header>
//               <Card.Body>
//                 <Row>
//                   <Col xl={4} lg={6} className="mb-3">
//                     <RHFTextField
//                       name="MRP"
//                       label="Maximum Retail Price"
//                       placeholder="₹ 00.00"
//                       required
//                       onChange={(e) => {
//                         const priceInput = e.target.value;
//                         if (!isNaN(priceInput)) {
//                           if (priceInput >= Number(getValues("sellingPrice")))
//                             setValue("MRP", priceInput, validateWith);
//                         }
//                       }}
//                     />
//                   </Col>
//                   <Col xl={4} lg={6} className="mb-3">
//                     <RHFTextField
//                       name="sellingPrice"
//                       label="Selling Price"
//                       placeholder="₹ 00.00"
//                       required
//                       type="number"
//                       onChange={(e) => {
//                         const priceInput = e.target.value;
//                         if (!isNaN(priceInput)) {
//                           if (priceInput <= Number(getValues("MRP")))
//                             setValue("sellingPrice", priceInput, validateWith);
//                         }
//                       }}
//                     />
//                   </Col>
//                   <Col xl={4} lg={6} className="mb-xl-3">
//                     <RHFTextField
//                       name="discount"
//                       value={discountValue}
//                       disabled={true}
//                       label="Discount"
//                       placeholder="₹ 00.00"
//                     />
//                   </Col>
//                 </Row>
//               </Card.Body>
//             </Card>

//             <SizeChartBuilder charts={sizeCharts} onChange={setSizeCharts} />

//             <Card>
//               <Card.Header>
//                 <div className="d-flex">
//                   <div className="flex-shrink-0 me-3">
//                     <div className="avatar-sm">
//                       <div className="avatar-title rounded-circle bg-light text-primary fs-20">
//                         <i className="ri-file-list-3-line"></i>
//                       </div>
//                     </div>
//                   </div>
//                   <div className="flex-grow-1 d-flex align-items-center">
//                     <h5 className="card-title mb-1">
//                       Product Details <span className="text-danger">*</span>{" "}
//                     </h5>
//                   </div>
//                 </div>
//               </Card.Header>
//               <Card.Body>
//                 <div className="mb-3">
//                   <Form.Label>
//                     Product Details <span className="text-danger">*</span>
//                   </Form.Label>
//                   <CKEditor
//                     defaultValue={getValues("productDetails")}
//                     value={getValues("productDetails")}
//                     editor={ClassicEditor}
//                     data={getValues("productDetails")}
//                     onChange={(event, editor) =>
//                       setValue("productDetails", editor.getData(), validateWith)
//                     }
//                   />
//                   {errors.productDetails?.message && (
//                     <div className="text-denger">
//                       {errors.productDetails?.message}
//                     </div>
//                   )}

//                   {/* <RHTextarea name='productDetails' placeholder="Must enter minimum of a 100 characters" rows={3} /> */}
//                 </div>
//               </Card.Body>
//             </Card>

//             <Card>
//               <Card.Header>
//                 <div className="d-flex">
//                   <div className="flex-shrink-0 me-3">
//                     <div className="avatar-sm">
//                       <div className="avatar-title rounded-circle bg-light text-primary fs-20">
//                         <i className="ri-search-eye-line"></i>
//                       </div>
//                     </div>
//                   </div>
//                   <div className="flex-grow-1 d-flex align-items-center">
//                     <h5 className="card-title mb-1">
//                       Search Engine Optimization (SEO)
//                     </h5>
//                   </div>
//                 </div>
//               </Card.Header>
//               <Card.Body>
//                 <Row>
//                   <Col lg={6} className="mb-3">
//                     {/* <Form.Label htmlFor="product-title-input">Meta Title</Form.Label>
//                                         <Form.Control type="hidden" id="formAction" name="formAction" defaultValue="add" />
//                                         <Form.Control type="text" className="d-none" id="product-id-input" />
//                                         <Form.Control type="text" id="product-title-input" defaultValue="" placeholder="Add a meta title" required />
//                                         <div className="invalid-feedback">Please enter a meta title.</div> */}
//                     <RHFTextField
//                       name="metaTitle"
//                       label="Meta Title"
//                       placeholder="Add a meta title"
//                     />
//                   </Col>
//                   <Col lg={6} className="mb-3">
//                     {/* <Form.Label htmlFor="product-title-input">Meta Tag</Form.Label>
//                                         <Form.Control type="hidden" id="formAction" name="formAction" defaultValue="add" />
//                                         <Form.Control type="text" className="d-none" id="product-id-input" />
//                                         <Form.Control type="text" id="product-title-input" defaultValue="" placeholder="Add a meta tag" required />
//                                         <div className="invalid-feedback">Please enter a meta tag.</div> */}
//                     <RHFTextField
//                       name="metaTag"
//                       label="Meta Tag"
//                       placeholder="Add a meta metaTag"
//                     />
//                   </Col>
//                   <Col className="mb-xl-3">
//                     {/* <Form.Group>
//                                             <Form.Label>Meta Description</Form.Label>
//                                             <textarea className="form-control" id="exampleFormControlTextarea5" placeholder="Add meta description" rows={3}></textarea>
//                                         </Form.Group>
//                                         <div className="invalid-feedback">Please enter meta dscription.</div> */}
//                     <RHTextarea
//                       label="Meta Description"
//                       name="metaDescription"
//                       placeholder="Add meta description"
//                       rows={3}
//                     />
//                   </Col>
//                 </Row>
//               </Card.Body>
//             </Card>
//           </Col>

//           <Col xl={3} lg={4}>
//             <Card>
//               <Card.Header>
//                 <h5 className="card-title mb-0">
//                   Product Category<span className="text-danger">*</span>
//                 </h5>
//               </Card.Header>
//               <Card.Body>
//                 <CategoryTree
//                   // error={errors.catId?.message}
//                   data={categoryTreeData?.response}
//                   defaultChecked={false}
//                   className="product-parent-category"
//                 />
//               </Card.Body>
//             </Card>

//             {/* <Card>
//                             <Card.Header>
//                                 <h5 className="card-title mb-0">Publish Schedule</h5>
//                             </Card.Header>

//                             <Card.Body>
//                                 <div>
//                                     <Form.Label htmlFor="datepicker-publish-input">Publish Date & Time</Form.Label>
//                                     <Flatpickr
//                                         className="form-control flatpickr-input"
//                                         placeholder='Enter publish date'
//                                         options={{
//                                             dateFormat: "d M, Y",
//                                         }}
//                                         onChange={(e, date) => {
//                                             const formattedDate = moment(e?.[0]).format('L');

//                                             setValue('publishTime', e?.[0], validateWith)

//                                         }}
//                                     />
//                                 </div>
//                             </Card.Body>
//                         </Card> */}

//             {/* <Card>
//                             <Card.Header>
//                                 <h5 className="card-title mb-0">Product Tags</h5>
//                             </Card.Header>
//                             <Card.Body>
//                                 <div className="hstack gap-3 align-items-start">
//                                     <div className="flex-grow-1">
//                                         <RHFTextField name='metaTag' placeholder="Enter tags" />
//                                     </div>
//                                 </div>
//                             </Card.Body>
//                         </Card> */}

//             <Card>
//               <Card.Header>
//                 <h5 className="card-title mb-0">
//                   Product Short Description
//                   <span className="text-danger">*</span>
//                 </h5>
//               </Card.Header>
//               <Card.Body>
//                 <p className="text-muted mb-2">
//                   Add short description for product
//                 </p>
//                 <RHTextarea
//                   name="shortDescription"
//                   placeholder="Must enter minimum of a 100 characters"
//                   rows={3}
//                 />
//                 {/* <textarea className="form-control" placeholder="Must enter minimum of a 100 characters" rows={3}></textarea> */}
//               </Card.Body>
//             </Card>

//             <div className="d-flex ">
//               <Form.Check
//                 onChange={(e) => {
//                   setValue("isHot", e.target.checked, validateWith);
//                 }}
//                 className="mt-0 me-2"
//                 type="checkbox"
//                 defaultValue=""
//                 id="isHot"
//                 aria-label="Checkbox for following text input"
//                 checked={getValues("isHot") === true}
//               />
//               <label htmlFor="isHot">Hot Product</label>
//             </div>

//             <div className="d-flex ">
//               <Form.Check
//                 onChange={(e) => {
//                   setValue("isTrending", e.target.checked, validateWith);
//                 }}
//                 className="mt-0 me-2"
//                 type="checkbox"
//                 defaultValue=""
//                 id="isTrending"
//                 aria-label="Checkbox for following text input"
//                 checked={getValues("isTrending") === true}
//               />
//               <label htmlFor="isTrending">Trending Product</label>
//             </div>


//             <div className="d-flex ">
//               <Form.Check
//                 onChange={(e) => {
//                   setValue("isShowInDesigner", e.target.checked, validateWith);
//                 }}
//                 className="mt-0 me-2"
//                 type="checkbox"
//                 defaultValue=""
//                 id="isShowInDesigner"
//                 aria-label="Checkbox for following text input"
//                 checked={getValues("isShowInDesigner") === true}
//               />
//               <label htmlFor="isShowInDesigner">Show In Designer</label>
//             </div>
//           </Col>
//         </Row>
//         <Container fluid>
//           <div className="d-flex">
//             <div className="ms-auto">
//               <button
//                 className="btn btn-info shadow-lg rounded"
//                 type="submit"
//                 onClick={() => {
//                   setIsExit(true);
//                 }}
//               >
//                 Draft & Exit
//               </button>
//               <button
//                 className="btn  btn-danger shadow-lg rounded ms-2"
//                 onClick={() => navigate("/products-list")}
//               >
//                 Cancel
//               </button>
//               <button
//                 className="btn btn-secondary shadow-lg rounded ms-2"
//                 type="submit"
//                 disabled={isLoading}
//               >
//                 Next
//               </button>
//             </div>
//           </div>
//         </Container>
//       </FormProvider>
//     </>
//   );
// };

// export default StepOneProduct;
